import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  walletTransactionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type { TransactionDto } from "@/features/wallet/api/dto";

/**
 * Annule par l'expéditeur un transfert vers Non-Member **non encaissé**
 * (`POST /Transactions/{id}/cancel`, guide v2.4 §6.3).
 *
 * Le code est annulé et les fonds retournés au wallet source via une
 * transaction `NONMEMBER_EXPIRY_REFUND` (frais 0). Codes : 404 hors
 * périmètre ; 422 déjà encaissé, clôturé, ou transaction sans code.
 */
export const useCancelTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "transactions", "cancel"],
    mutationFn: (transactionId: string) =>
      mutateData({
        url: walletEndpoints.transactionsExt.cancel(transactionId),
        method: "POST",
      }) as Promise<TransactionDto>,
    onSuccess: (_data, transactionId) =>
      invalidateAfterMutation(
        queryClient,
        walletTransactionCacheKeys(transactionId)
      ),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCancelTransaction failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
